import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { JSX } from 'react';
import type { PublicPropertySummary, PublishedUnitSummary } from '@/lib/types/publicProperty';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import {
  formatAvailabilityDateLabel,
  formatPropertyAddress,
  formatSquareFeet,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';
import { applyTemplate } from '@/lib/utils/textTemplates';
import { formatAssetTypeLabel } from '@/lib/constants/propertyOptions';
import { isPropertyMediaVideoUrl } from '@/lib/storage/propertyMedia';

export interface PublicUnitCardProps {
  property: PublicPropertySummary;
  unit: PublishedUnitSummary;
  href: string;
  className?: string;
}

export function PublicUnitCard({
  property,
  unit,
  href,
  className,
}: PublicUnitCardProps): JSX.Element {
  const siteSettings = useSiteSettingsDetail().data;
  const unitCardLabels = siteSettings?.publicContent.shared.unitCard;
  const unitDetailLabels = siteSettings?.publicContent.unitDetail.labels;
  const mapUnitRowContent = siteSettings?.publicContent.availabilitiesPage.mapUnitRow;
  const resolveLabel = (value: string | undefined, fallback: string) =>
    typeof value === 'string' && value.trim().length > 0 ? value : fallback;
  const availabilityLabels = {
    immediateLabel: resolveLabel(unitDetailLabels?.immediateLabel, 'Immediate'),
    negotiableLabel: resolveLabel(unitDetailLabels?.negotiableLabel, 'Negotiable'),
  };
  const unitLabelTemplate = resolveLabel(mapUnitRowContent?.unitLabelTemplate, 'Unit {unitLabel}');
  const levelLabelTemplate = resolveLabel(mapUnitRowContent?.levelLabelTemplate, '{floor}');
  const heroImageUrl = resolveCoverImage(property, unit);
  const addressLabel = formatPropertyAddress(property.address);
  const assetLabel = formatAssetTypeLabel(property.assetType);
  const readinessLabel = PUBLIC_READINESS_LABELS[unit.availabilityStatus];
  const availabilityLabel = formatAvailabilityLabel(unit, availabilityLabels);
  const areaLabel = formatSquareFeet(unit.areaSF);
  const unitLabel = applyTemplate(unitLabelTemplate, {
    unitLabel: formatUnitLabel(unit),
    unitCode: unit.unitCode,
  });
  const rawFloorValue = applyTemplate(levelLabelTemplate, {
    level: unit.floorLevel,
    floor: unit.floorLevel,
    floorLevel: unit.floorLevel,
  });
  const floorValue = rawFloorValue.trim().length > 0 ? rawFloorValue : unit.floorLevel;
  const availabilityFallback = resolveLabel(unitCardLabels?.availabilityFallback, 'Available now');
  const availabilityText =
    availabilityLabel.trim().length > 0 ? availabilityLabel : availabilityFallback;
  const availabilityLabelText = resolveLabel(unitCardLabels?.availabilityLabel, 'Availability');
  const floorLabelText = resolveLabel(unitCardLabels?.floorLabel, 'Floor');
  const viewDetailsLabel = resolveLabel(unitCardLabels?.viewDetailsLabel, 'View details');
  const divisibleLabel = resolveLabel(unitCardLabels?.divisibleLabel, 'Divisible');
  const photoFallback = resolveLabel(unitCardLabels?.photoFallback, '');

  return (
    <article
      className={clsx(
        'cv-auto group flex h-full flex-col overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-card transition duration-200 focus-within:ring-2 focus-within:ring-brand-accent hover:-translate-y-1 hover:shadow-lg',
        className,
      )}
      data-testid="public-unit-card"
    >
      <Link
        to={href}
        className="flex h-full flex-col focus:outline-none"
        aria-label={`View unit ${formatUnitLabel(unit)} at ${addressLabel}`}
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-surface-subtle">
          {heroImageUrl ? (
            isPropertyMediaVideoUrl(heroImageUrl) ? (
              <video
                src={heroImageUrl}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                muted
                playsInline
                preload="metadata"
                aria-label={`Unit ${formatUnitLabel(unit)} at ${addressLabel}`}
              />
            ) : (
              <img
                src={heroImageUrl}
                alt={`Unit ${formatUnitLabel(unit)} at ${addressLabel}`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            )
          ) : (
            <div
              className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-primary/20 via-brand-primary/10 to-brand-accent/10 text-[0.7rem] font-semibold uppercase tracking-wide text-white/70"
              aria-hidden={photoFallback.length === 0}
            >
              {photoFallback}
            </div>
          )}

          <div
            className="absolute left-4 top-4 inline-flex items-center gap-xs rounded-full bg-black/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow"
            data-ui="Public.UnitCard.AssetType"
          >
            {assetLabel}
          </div>
          <div className="absolute right-4 top-4 inline-flex items-center gap-xs rounded-full bg-black/60 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-white shadow">
            {readinessLabel}
          </div>
          {unit.potentiallyDivisible ? (
            <div className="absolute bottom-4 left-4 inline-flex items-center gap-xs rounded-full bg-white/90 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-brand-primary shadow">
              {divisibleLabel}
            </div>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-3 p-5">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <p className="text-lg font-semibold leading-snug text-brand-primary">
                {addressLabel}
              </p>
              <p className="text-sm font-semibold text-text-primary/70">{unitLabel}</p>
            </div>

            <div className="flex items-baseline justify-between gap-3 text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              <span>{property.submarket}</span>
              <span>{areaLabel} sq ft</span>
            </div>
          </div>

          <dl className="flex items-start justify-between gap-6 text-xs uppercase tracking-wide text-text-primary/60">
            <div className="flex flex-col gap-xs">
              <dt>{availabilityLabelText}</dt>
              <dd className="text-sm font-semibold text-brand-primary">{availabilityText}</dd>
            </div>
            <div className="flex flex-col gap-xs text-right">
              <dt>{floorLabelText}</dt>
              <dd className="text-sm font-semibold text-text-primary">{floorValue}</dd>
            </div>
          </dl>

          <div className="mt-auto flex items-center justify-between pt-1">
            <span className="text-sm font-semibold text-brand-accent">{viewDetailsLabel}</span>
            <span aria-hidden className="text-brand-accent transition group-hover:translate-x-1">
              →
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

function resolveCoverImage(property: PublicPropertySummary, unit: PublishedUnitSummary): string {
  if (typeof unit.heroImageUrl === 'string' && unit.heroImageUrl.trim().length > 0) {
    return unit.heroImageUrl.trim();
  }

  const propertyHero = property.heroMediaUrls[0];
  if (typeof propertyHero === 'string' && propertyHero.length > 0) {
    return propertyHero;
  }

  if (typeof property.coverImageUrl === 'string' && property.coverImageUrl.length > 0) {
    return property.coverImageUrl;
  }

  return '';
}

function formatAvailabilityLabel(
  unit: PublishedUnitSummary,
  labels: { immediateLabel: string; negotiableLabel: string },
): string {
  const availabilityDate = formatAvailabilityDateLabel(unit.availabilityStart);
  if (availabilityDate) {
    return availabilityDate;
  }
  if (unit.possessionTimeline === 'negotiable') {
    return labels.negotiableLabel;
  }
  return labels.immediateLabel;
}
